import { MapboxDrawControl } from '@components/map/layers/draw/polygons/DrawPolygonControl';
import { MapMouseEvent } from 'mapbox-gl';
import { RefObject, useEffect, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';

import { useFormMapContext } from './FormMapContext';

export type MapCursorMode = 'create' | 'edit';

export const useDrawMapCursor = (
  mode: MapCursorMode,
  drawRef: RefObject<MapboxDrawControl | null>,
  isInteractiveFeature: (layerId: string) => boolean,
) => {
  const mapRef = useMap();
  const [cursor, setCursor] = useState<'default' | 'pointer' | 'crosshair'>('default');

  useEffect(() => {
    const map = mapRef.current;
    const draw = drawRef.current;
    if (!map || !draw) return;

    const onMouseMove = (e: MapMouseEvent) => {
      const features = map.queryRenderedFeatures(e.point);
      const hovering = features.some((f) => f.layer && isInteractiveFeature(f.layer.id));
      const isCreating = draw.getMode() === 'draw_polygon';

      if (isCreating) {
        setCursor(hovering ? 'pointer' : 'crosshair');
      } else {
        setCursor(hovering ? 'pointer' : 'default');
      }
      map.getCanvas().style.cursor = cursor;
    };

    map.on('mousemove', onMouseMove);

    return () => {
      map.off('mousemove', onMouseMove);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (map.getCanvas()) {
        map.getCanvas().style.cursor = '';
      }
    };
  }, [cursor, drawRef, isInteractiveFeature, mapRef, mode]);

  return cursor;
};

export const useMapCursor = () => {
  const mapRef = useMap();
  const [cursor, setCursor] = useState<'default' | 'pointer' | 'crosshair'>('default');
  const { isSelectionEnabled } = useFormMapContext();

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const onMouseMove = () => {
      setCursor(isSelectionEnabled ? 'crosshair' : 'default');
      map.getCanvas().style.cursor = cursor;
    };

    map.on('mousemove', onMouseMove);

    return () => {
      map.off('mousemove', onMouseMove);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (map.getCanvas()) {
        map.getCanvas().style.cursor = '';
      }
    };
  }, [cursor, isSelectionEnabled, mapRef]);

  return cursor;
};
